import type { SupabaseClient } from '@supabase/supabase-js'
import type { IProfileRepository, Profile } from '@knownow/domain'
import { NotFoundError } from '@knownow/domain'
import type { Database } from '@knownow/db'

type ProfileRow = Database['org']['Tables']['profiles']['Row']

export class SupabaseProfileRepository implements IProfileRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getByUserId(userId: string): Promise<Profile> {
    const { data, error } = await this.client
      .schema('org')
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error !== null || data === null) {
      throw new NotFoundError('Profile', userId)
    }

    return this.toDomain(data)
  }

  async update(profile: Profile): Promise<void> {
    const { error } = await this.client
      .schema('org')
      .from('profiles')
      .update({
        display_name: profile.displayName,
        avatar_url: profile.avatarUrl,
        default_organization_id: profile.defaultOrganizationId,
        completed_at: profile.completedAt?.toISOString() ?? null,
        updated_at: profile.updatedAt.toISOString(),
      })
      .eq('user_id', profile.userId)

    if (error !== null) {
      throw error
    }
  }

  private toDomain(row: ProfileRow): Profile {
    return {
      userId: row.user_id,
      displayName: row.display_name,
      avatarUrl: row.avatar_url,
      defaultOrganizationId: row.default_organization_id,
      completedAt: row.completed_at ? new Date(row.completed_at) : null,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    }
  }
}
